import { useState, useEffect, useCallback } from 'react';
import { ApolloProvider } from '@apollo/client';
import apolloClient, { ON_NEW_AUDIO_SUBSCRIPTION } from './apolloClient';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import AudioPlayer from './components/AudioPlayer';
import AudioHistory from './components/AudioHistory';
import AuthComponents from './components/AuthComponents';
import UserProfile from './components/UserProfile';
import DarkModeToggle from './components/DarkModeToggle';
import BuyMeCoffee from './components/BuyMeCoffee';
import TabNavigation, { TabType } from './components/TabNavigation';
import PluginSection from './components/PluginSection';
import FuturePlans from './components/FuturePlans';
import './App.css';
import SessionConfig from './components/SessionConfig';


const config = window.KOREADER_TTS_CONFIG || {};
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.USER_POOL_ID || process.env.REACT_APP_USER_POOL_ID || '',
      userPoolClientId: config.USER_POOL_CLIENT_ID || process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
    },
  },
  API: {
    GraphQL: {
      endpoint: config.APPSYNC_URL || process.env.REACT_APP_APPSYNC_URL || '',
      region: config.AWS_REGION || process.env.REACT_APP_AWS_REGION || 'us-east-1',
      defaultAuthMode: 'userPool',
    },
  },
});
const client = generateClient({authMode: 'userPool'});


function App() {
  const [apiKey, setApiKey] = useState<string>('');
  const [ttsConfig, setTtsConfig] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [timestamp, setTimestamp] = useState<string | null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  const [subscriptionClient, setSubscriptionClient] = useState<any>(null);
  const [audioUnlocked, setAudioUnlocked] = useState<boolean>(false);
  const [context, setContext] = useState<AudioContext | null>(null);
  const [user, setUser] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pluginVersion, setPluginVersion] = useState<{version: string; releaseDate: string; changelog: string[]} | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('plugin');

  // --- Check auth ---
  const checkAuthState = useCallback(async () => {
    try {
      const userData = await getCurrentUser();
      const signedInUser = {
        username: userData.signInDetails?.loginId,
        isSignedIn: true,
      };
      setUser(signedInUser);
      await initializeUserConfig();
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuthState();
    fetchPluginVersion();
  }, [checkAuthState]);

  const fetchPluginVersion = async () => {
    try {
      const response = await fetch('/version.json');
      const data = await response.json();
      setPluginVersion(data);
    } catch (err) {
      console.error('Failed to fetch plugin version:', err);
    }
  };

  // --- User Config Management ---
  const initializeUserConfig = async () => {
    try {
      console.log('🔍 Fetching user config...');

      // Ensure tokens are available before any GraphQL request
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        throw new Error('No access token found; user session invalid.');
      }

      // Fetch user config (apiKey and ttsConfig)
      const getUserConfigQuery = /* GraphQL */ `
        query GetUserConfig {
          getUserConfig {
            userId
            apiKey
            ttsConfig
            createdAt
          }
        }
      `;
      console.log('📡 Fetching user config...');
      const result: any = await client.graphql({query: getUserConfigQuery, authToken: session.tokens.accessToken.toString()});
      console.log('📥 getUserConfig result:', JSON.stringify(result, null, 2));

      const userConfig = result?.data?.getUserConfig;

      if (userConfig && userConfig.apiKey) {
        console.log('✅ Found user config with API key:', userConfig.apiKey);
        setApiKey(userConfig.apiKey);

        if (userConfig.ttsConfig) {
          setTtsConfig(userConfig.ttsConfig);
        }
      } else {
        console.error('❌ No user config found. User may need to sign out and sign in again.');
      }

    } catch (err) {
      console.error('🔥 Error fetching user config:', err);
    }
  };


  // --- Auth success handler ---
  const handleAuthSuccess = async (user: any) => {
    setUser(user);
    setShowAuthModal(false);
    await initializeUserConfig();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    }
    setUser(null);
    setApiKey('');
    setTtsConfig(null);
  };

  // --- Audio Unlock ---
  const handleEnableAudio = async () => {
    try {
      let ctx = context;
      if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();

      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);

      console.log('✅ Audio unlocked');
      setContext(ctx);
      setAudioUnlocked(true);
    } catch (err) {
      console.error('❌ Failed to unlock audio:', err);
    }
  };

  // --- Subscription ---
  useEffect(() => {
    if (audioUnlocked && apiKey) subscribeToAudio(apiKey);
  }, [audioUnlocked, apiKey]);

  const subscribeToAudio = async (key: string) => {
    console.log(`Subscribing to audio for API key: ${key}`);
    try {
      // Ensure tokens are available before any GraphQL request
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        throw new Error('No access token found; user session invalid.');
      }

      const subscription = await client.graphql( { query: ON_NEW_AUDIO_SUBSCRIPTION, variables: { sessionId: key }, authToken: session.tokens.accessToken.toString() } );

      if ('subscribe' in subscription) {
        const sub = subscription.subscribe({
          next: (payload: any) => {
            const data = payload.value?.data || payload.data;
            if (data && data.onNewAudio) {
              // Check if this is an error notification
              if (data.onNewAudio.error) {
                console.error('TTS Error received:', data.onNewAudio.error);
                setErrorMessage(data.onNewAudio.errorMessage || 'An error occurred processing your request. Please try again.');
                setAudioUrl(null);
                setTimestamp(data.onNewAudio.timestamp);
                setTextPreview(data.onNewAudio.textPreview || null);
                // Clear error after 10 seconds
                setTimeout(() => setErrorMessage(null), 10000);
              } else {
                // Success - clear any previous error and set audio
                setErrorMessage(null);
                setAudioUrl(data.onNewAudio.audioUrl);
                setTimestamp(data.onNewAudio.timestamp);
                setTextPreview(data.onNewAudio.textPreview || null);
              }
              // Trigger refresh of audio history and quota
              setRefreshTrigger(prev => prev + 1);
            }
          },
          error: (err: any) => {
            console.error('Subscription error:', err);
          },
        });
        setSubscriptionClient(sub);
      }
    } catch (error) {
      console.error('Error setting up subscription:', error);
    }
  };

  useEffect(() => {
    return () => subscriptionClient?.unsubscribe();
  }, [subscriptionClient]);

  // --- UI ---
  return (
    <div className="App">
      <div className="top-right-actions">
        <BuyMeCoffee />
        <DarkModeToggle />
      </div>
      <header className="App-header">
        <div className="header-title">
          <img src="/alouditlogo.png" alt="Aloudit Logo" className="app-logo" />
        </div>
        <p>A text-to-speech service for KOReader devices. Aloudit receives text from your reader, generates high-quality audio, and plays it automatically. One quick setup, and it will feel as though your device has built-in TTS support.</p>
        <p className="disclaimer"><small>⚠️ This is an externally developed plugin, not officially associated with KOReader.</small></p>

        {user ? (
          <div className="user-section">
            <div className="user-info-group">
              <span className="user-greeting">Hello, {user.username}</span>
              <UserProfile user={user} onSignOut={handleSignOut} refreshTrigger={refreshTrigger} />
            </div>
            <button className="logout-button" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        ) : (
          <button className="auth-button" onClick={() => setShowAuthModal(true)}>
            Sign In
          </button>
        )}
      </header>

      {showAuthModal && (
        <div className="auth-modal">
          <AuthComponents
            onAuthSuccess={handleAuthSuccess}
            onCancel={() => setShowAuthModal(false)}
          />
        </div>
      )}

      {!audioUnlocked && user ? (
        <div className="session-display">
          <h2>Audio Playback Permission Required</h2>
          <p>
            Please enable audio playback to allow automatic voice responses.
            This is required by your browser.
          </p>
          <button onClick={handleEnableAudio} disabled={!user}>
            {user ? 'Enable Audio' : 'Sign in to enable audio'}
          </button>
        </div>
      ) : (
        <>
          {errorMessage && (
            <div className="error-notification">
              <h3>⚠️ Processing Error</h3>
              <p>{errorMessage}</p>
              <p className="error-hint">Your quota has not been charged. Please try sending the text again.</p>
            </div>
          )}

          <AudioPlayer audioUrl={audioUrl} timestamp={timestamp} textPreview={textPreview} />

          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

          <div className="tab-content">
            {activeTab === 'plugin' && (
              <PluginSection apiKey={apiKey} pluginVersion={pluginVersion} isLoggedIn={!!user} />
            )}
            {activeTab === 'config' && (
              <SessionConfig
                apiKey={apiKey}
                initialConfig={ttsConfig}
                onConfigSave={(config) => setTtsConfig(config)}
                isLoggedIn={!!user}
              />
            )}
            {activeTab === 'history' && (
              <AudioHistory
                apiKey={apiKey}
                onSelectAudio={(url, ts, preview) => {
                  setAudioUrl(url);
                  setTimestamp(ts);
                  setTextPreview(preview || null);
                }}
                refreshTrigger={refreshTrigger}
                isLoggedIn={!!user}
              />
            )}
            {activeTab === 'future' && (
              <FuturePlans />
            )}
          </div>
        </>
      )}

      <footer>
        <p>Aloudit &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

const AppWithApollo = () => (
  <ApolloProvider client={apolloClient}>
    <App />
  </ApolloProvider>
);

export default AppWithApollo;
